import type { StyledCaption, StyledSubtitle } from '#types/styles'

/**
 * Extracts caption-related props from a content object, applying `subtitleColor`
 * as the default `captionColor` when no explicit caption color is provided.
 *
 * Intended to be spread directly onto `<cms-base-caption>` via `v-bind`.
 *
 * @param obj - Combined caption and subtitle style fields from a CMS content object.
 * @param obj.subtitleColor - Fallback color used for the caption when `captionColor` is absent.
 * @param obj.captionColor - Explicit caption color; defaults to `subtitleColor` when omitted.
 * @param obj.captionPosition - Position of the caption overlay (e.g. `'bottom-left'`).
 * @param obj.captionStyle - Typography / style token applied to the caption text.
 * @param obj.captionText - The caption string to render.
 * @param obj.captionHtml - Rich-text HTML content for the caption, used when `captionUseHtml` is `true`.
 * @param obj.captionUseHtml - When `true`, renders `captionHtml` instead of the plain `captionText`.
 * @returns An object with two keys:
 *   - `hasCaption` — `true` when both `captionText` and `captionPosition` are present; use as the `v-if` guard.
 *   - `captionProps` — the `StyledCaption` fields ready to spread onto `<cms-base-caption>` via `v-bind`.
 * @example
 * ```ts
 * const { hasCaption, captionProps } = getCaptionConfig(content)
 * // <cms-base-caption v-if="hasCaption" v-bind="captionProps" />
 * ```
 * @category Utils
 */
export const getCaptionConfig = ({
  subtitleColor,
  captionColor = subtitleColor,
  captionPosition,
  captionStyle,
  captionText,
  captionHtml,
  captionUseHtml,
}: StyledCaption & StyledSubtitle) => ({
  hasCaption: !!((captionText || captionHtml) && captionPosition),
  captionProps: {
    captionColor,
    captionPosition,
    captionStyle,
    captionText,
    captionHtml,
    captionUseHtml,
  },
})
